import { FC, Fragment, useEffect, useState } from 'react';

import { Button, Checkbox, Form, Row, Select } from 'antd';
import { useIntl } from 'react-intl';
import {
  CustomInputText,
  CustomOnlyNumber,
  CustomSelector,
} from 'src/components/forms/pin/CustomInputText';
import { ComboData, IAddressData, IAddressDataChile } from 'src/shared/models';
import {
  customRegexName,
  phoneRegex,
  rutFormat,
  useWindowSize,
  directionCharacters,
} from 'utils';
import { IAddressFormValues } from 'src/components/forms/interfaces';

interface Props {
  address?: IAddressDataChile;
  isEditForm?: boolean;
  isFirstAddress?: boolean;
  onCreateAddress?: (address: IAddressData) => void;
  onUpdateAddress?: (address: IAddressData) => void;
  regionList: ComboData[];
  districtList: ComboData[];
  isLoading?: boolean;
}

const AddressFormCl: FC<Props> = ({
  address,
  isEditForm,
  isFirstAddress,
  onCreateAddress,
  onUpdateAddress,
  regionList,
  districtList,
  isLoading = false,
}): JSX.Element => {
  const [form] = Form.useForm();
  const { isMD } = useWindowSize();
  const { formatMessage } = useIntl();
  const [addressData, setAddressData] = useState(address);
  const [districtListFiltered, setDistrictListFiltered] = useState<ComboData[]>(
    [],
  );

  const formValues: Partial<IAddressDataChile> = {
    name: address?.name || '',
    surname: address?.surname || '',
    zipCode: address?.zipCode || '',
    address: address?.address || '',
    phone: address?.phone || '',
    city: address?.city || '',
    regionName: address?.regionName || '',
    district: address?.district || '',
  };

  const orderRegions = (regionList): ComboData[] => {
    return regionList.sort(
      (a, b) => parseInt(a.value, 10) - parseInt(b.value, 10),
    );
  };

  const formFields: IAddressFormValues[] = [
    {
      type: 'text',
      name: 'name',
      placeholder: 'form.field.name',
      handleChange: 'name',
      initialValue: formValues.name,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.name' }) },
        ),
      },
      autocomplete: 'given-name',
      length: 25,
    },
    {
      type: 'text',
      name: 'surname',
      placeholder: 'form.field.last-name',
      handleChange: 'surname',
      initialValue: formValues.surname,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.last-name' }) },
        ),
      },
      autocomplete: 'family-name',
      length: 50,
    },
    {
      type: 'text',
      name: 'address',
      placeholder: 'form.field.address',
      handleChange: 'address',
      initialValue: formValues.address,
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      autocomplete: 'street-address',
      length: 40,
    },
    {
      type: 'number',
      name: 'phone',
      placeholder: 'form.field.telephone',
      handleChange: 'phone',
      initialValue: formValues.phone,
      rules: {
        pattern: phoneRegex.CL,
        message: formatMessage({
          id: 'form.field.phone-error-number-cl',
        }),
      },
      autocomplete: 'tel',
      length: 9,
    },
    {
      type: 'text',
      name: 'zipCode',
      placeholder: 'form.field.rut',
      handleChange: 'zipCode',
      initialValue: formValues.address,
      rules: {
        pattern: rutFormat,
        message: formatMessage(
          { id: 'form.field.error-rut-format' },
          { name: formatMessage({ id: 'form.field.rut' }) },
        ),
      },
      autocomplete: 'postal-code',
      length: 9,
    },
    {
      type: 'text',
      name: 'city',
      placeholder: 'form.field.city',
      handleChange: 'city',
      initialValue: formValues.city,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.city' }) },
        ),
      },
      autocomplete: 'address-level2',
      length: 50,
    },
    {
      type: 'regionSelector',
      name: 'regionName',
      placeholder: 'form.field.address.state-region',
      handleChange: 'regionName',
      initialValue: formValues.regionName,
      rules: {
        validator: async (_, regionName) => {
          if (!regionName) {
            throw new Error(
              formatMessage({
                id: 'form.field.state-is-required',
              }),
            );
          }
        },
      },
      value: addressData?.regionName,
    },
    {
      type: 'districtSelector',
      name: 'district',
      placeholder: 'form.field.comuna',
      handleChange: 'district',
      initialValue: formValues.district,
      rules: {
        validator: async (_, district) => {
          if (!district) {
            throw new Error(
              formatMessage({
                id: 'form.field.state-is-required',
              }),
            );
          }
        },
      },
      value: addressData?.district,
    },
  ];

  useEffect(() => {
    checkCanContinue();
  });

  useEffect(() => {
    form.resetFields();
    if (!addressData) return;
    form.setFieldsValue(formValues);
  }, []);

  useEffect(() => {
    if (!isFirstAddress) return;

    setAddressData((currentAddress) => {
      return {
        ...currentAddress,
        defaultAddress: true,
      };
    });
  }, []);

  const isDefaultAddressChecked = (): boolean => {
    if ((!isEditForm && isFirstAddress) || address?.defaultAddress) {
      return true;
    }
    return false;
  };

  const isDefaultAddressDisabled = () => {
    const editingDefaultAddress = Boolean(isEditForm && address.defaultAddress);

    if (isFirstAddress || editingDefaultAddress) return true;
    return false;
  };

  const handleSubmit = () => {
    isEditForm ? onUpdateAddress(addressData) : onCreateAddress(addressData);
    form.resetFields();
  };

  const checkAddressForm = (addressData: IAddressDataChile): boolean => {
    return Boolean(
      addressData?.address &&
        addressData?.city?.match(customRegexName) &&
        addressData?.name?.match(customRegexName) &&
        addressData?.phone?.match(phoneRegex.CL) &&
        addressData?.regionName &&
        addressData?.surname?.match(customRegexName) &&
        addressData?.zipCode?.match(rutFormat) &&
        addressData?.district,
    );
  };

  const checkCanContinue = () => {
    const isDataCorrect = checkAddressForm(addressData);

    if (addressData?.canContinue == isDataCorrect) return;
    setAddressData((data) => {
      return {
        ...data,
        canContinue: isDataCorrect,
      };
    });
  };

  // Districts here are used to store "comuna" value
  // An example of "region" would be "01"
  // And an example of "district" of that region would be "01.1"
  const filterDisctrictsByRegion = (event: string) => {
    let districtListFilter: ComboData[] = districtList.filter(
      (district, index) => {
        return district.value?.slice(0, 2) === event;
      },
    );
    return districtListFilter;
  };

  const setDistrictsByRegion = (event: string) => {
    const districtListFilter = filterDisctrictsByRegion(event);

    setDistrictListFiltered(districtListFilter);
  };

  const handleChange = (field: string) => (event: any) => {
    if (event.persist) event.persist();

    setAddressData((data) => {
      const newData = { ...data };
      let value = event?.target?.value;

      switch (field) {
        case 'phone':
        case 'rut':
        case 'district':
          value = event;
          break;
        case 'regionName':
          // In case the regionName is changed, filter districts again
          form.resetFields(['district']);
          newData.district = undefined;
          setDistrictsByRegion(event);
          value = event;
          break;
        case 'defaultAddress':
          value = event.target.checked;
          break;
        default:
          value = event.target.value;
          break;
      }

      return { ...newData, [field]: value };
    });
  };

  const renderTitle = (
    <>
      {isMD && (
        <h4 className="address__form-title">
          {isEditForm
            ? formatMessage({ id: 'form.address.edit-title' })
            : formatMessage({ id: 'form.address.new-title' })}
        </h4>
      )}
      {!isMD && !isEditForm && (
        <h4 className="address__form-title">
          {formatMessage({ id: 'form.address.new-title' })}
        </h4>
      )}
    </>
  );

  const renderFields = formFields.map((field) => {
    let component: JSX.Element;
    let districtsToShow = filterDisctrictsByRegion(formValues.regionName);

    if (field.type === 'regionSelector' && field.initialValue) {
      if (field.initialValue.slice(0, 2))
        districtsToShow = filterDisctrictsByRegion(
          field.initialValue.slice(0, 2),
        );
    }

    switch (field.type) {
      case 'text':
        component = CustomInputText(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length,
          true,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      case 'number':
        component = CustomOnlyNumber(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length ?? 40,
          true,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      case 'regionSelector':
        component = CustomSelector(
          form,
          field.name,
          orderRegions(regionList),
          field.placeholder,
          'value',
          handleChange(field.name),
          false,
          'description',
          true,
          [field.rules],
          false,
          false,
        );
        break;
      case 'districtSelector':
        if (field.initialValue && field.initialValue.slice(0, 2))
          districtsToShow = filterDisctrictsByRegion(
            field.initialValue.slice(0, 2),
          );
        component = CustomSelector(
          form,
          field.name,
          districtListFiltered?.length ? districtListFiltered : districtsToShow,
          field.placeholder,
          'value',
          handleChange(field.name),
          false,
          'description',
          false,
          [field.rules],
          false,
          addressData ? !addressData.regionName : !formValues.regionName,
        );
        break;
      default:
        return;
    }
    return <Fragment key={field.name}>{component}</Fragment>;
  });

  return (
    <>
      {renderTitle}
      <Form
        name="addressForm"
        form={form}
        className="address__form"
        onFinish={handleSubmit}>
        {renderFields}
        {!isDefaultAddressDisabled() && (
          <Form.Item
            name="defaultAddress"
            valuePropName="checked"
            initialValue={isDefaultAddressChecked()}>
            <Checkbox
              className="address__form__button-wrapper"
              defaultChecked={isDefaultAddressChecked()}
              onChange={handleChange('defaultAddress')}
              disabled={isDefaultAddressDisabled()}>
              {formatMessage({ id: 'form.field.address.preferred' })}
            </Checkbox>
          </Form.Item>
        )}
        <Form.Item shouldUpdate className="address__form__button-wrapper">
          {() => (
            <Button
              htmlType="submit"
              className="address__form__button-submit btn-primary btn-primary--black"
              disabled={!addressData?.canContinue || isLoading}>
              {formatMessage({ id: 'form.address.add-new' })}
              {isMD && (
                <i className="icon icon--arrow-normal-right-white button-icon" />
              )}
            </Button>
          )}
        </Form.Item>
      </Form>
    </>
  );
};

export default AddressFormCl;
